import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import CreativeProjectPreviewShell from "./CreativeProjectPreviewShell";

import {
  childrensBookIllustrationsByUnit,
  currentChildrensBookCover,
} from "../utils/childrensBookAssetState";

function clean(value) {
  return String(value ?? "").trim();
}

function safeImageUrl(value) {
  const candidate = clean(value);

  if (!candidate) return "";

  try {
    const parsed = new URL(candidate);

    return (
      parsed.protocol === "http:" ||
      parsed.protocol === "https:"
    )
      ? parsed.toString()
      : "";
  } catch {
    return "";
  }
}

function protectedAsset(asset) {
  return Boolean(
    asset &&
    (
      asset.source === "upload" ||
      asset.storageProvider === "local"
    )
  );
}

export default function ChildrensBookBookPreview({
  lang = "en",
  projectPlan = {},
  childrensBookAssets = [],
  childrensBookAssetsLoading = false,
  childrensBookAssetsError = "",
  onLoadChildrensBookAssetFile,
  onOpenBookAssembly,
  onOpenManuscriptPlan,
  onOpenIllustrationPlan,
  onOpenCoverPlan,
  onExit,
}) {
  const isFR =
    String(lang).toLowerCase() === "fr";

  const bookTitle =
    clean(projectPlan.identity?.title) ||
    (
      isFR
        ? "Livre pour enfants sans titre"
        : "Untitled Children’s Book"
    );

  const authorName =
    clean(projectPlan.identity?.penName) ||
    clean(projectPlan.identity?.authorName) ||
    (
      isFR
        ? "Auteur non renseigné"
        : "Author not provided"
    );

  const units = useMemo(
    () =>
      Array.isArray(
        projectPlan.bookMap?.units
      )
        ? projectPlan.bookMap.units
        : [],
    [projectPlan.bookMap?.units]
  );

  const manuscriptEntries =
    useMemo(
      () =>
        Array.isArray(
          projectPlan
            .manuscriptPlan
            ?.entries
        )
          ? projectPlan
              .manuscriptPlan
              .entries
          : [],
      [
        projectPlan
          .manuscriptPlan
          ?.entries,
      ]
    );

  const manuscriptsByUnit =
    useMemo(
      () =>
        manuscriptEntries.reduce(
          (result, entry) => {
            const unitId =
              clean(entry?.unitId);

            if (unitId) {
              result[unitId] = entry;
            }

            return result;
          },
          {}
        ),
      [manuscriptEntries]
    );

  const activeCover =
    useMemo(
      () =>
        currentChildrensBookCover(
          childrensBookAssets,
          "front_cover"
        ),
      [childrensBookAssets]
    );

  const illustrationsByUnit =
    useMemo(
      () =>
        childrensBookIllustrationsByUnit(
          childrensBookAssets
        ),
      [childrensBookAssets]
    );

  const previewAssets =
    useMemo(() => {
      const rows = [
        activeCover,
        ...Object.values(
          illustrationsByUnit
        ),
      ].filter(Boolean);

      const seen = new Set();

      return rows.filter((asset) => {
        const id = clean(asset?.id);

        if (!id || seen.has(id)) {
          return false;
        }

        seen.add(id);
        return true;
      });
    }, [
      activeCover,
      illustrationsByUnit,
    ]);

  const [
    protectedPreviewUrls,
    setProtectedPreviewUrls,
  ] = useState({});

  const [
    protectedPreviewErrors,
    setProtectedPreviewErrors,
  ] = useState({});

  const [
    protectedPreviewsLoading,
    setProtectedPreviewsLoading,
  ] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];

    const assetsToLoad =
      previewAssets.filter(
        protectedAsset
      );

    setProtectedPreviewUrls({});
    setProtectedPreviewErrors({});

    if (
      assetsToLoad.length === 0 ||
      typeof onLoadChildrensBookAssetFile !==
        "function"
    ) {
      setProtectedPreviewsLoading(
        false
      );

      return () => {};
    }

    setProtectedPreviewsLoading(true);

    async function loadPreviews() {
      const nextUrls = {};
      const nextErrors = {};

      for (const asset of assetsToLoad) {
        const id = clean(asset?.id);
        const url =
          clean(asset?.publicUrl);

        if (!id || !url) continue;

        try {
          const blob =
            await onLoadChildrensBookAssetFile(
              url
            );

          if (cancelled) return;

          if (
            typeof URL === "undefined" ||
            typeof URL.createObjectURL !==
              "function"
          ) {
            throw new Error(
              "Object URL unavailable"
            );
          }

          const objectUrl =
            URL.createObjectURL(blob);

          objectUrls.push(objectUrl);
          nextUrls[id] = objectUrl;
        } catch {
          nextErrors[id] = isFR
            ? "Impossible de charger cette image enregistrée."
            : "Could not load this saved image.";
        }
      }

      if (!cancelled) {
        setProtectedPreviewUrls(
          nextUrls
        );
        setProtectedPreviewErrors(
          nextErrors
        );
      }
    }

    loadPreviews().finally(() => {
      if (!cancelled) {
        setProtectedPreviewsLoading(
          false
        );
      }
    });

    return () => {
      cancelled = true;

      if (
        typeof URL !== "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        objectUrls.forEach(
          (url) =>
            URL.revokeObjectURL(url)
        );
      }
    };
  }, [
    previewAssets,
    onLoadChildrensBookAssetFile,
    isFR,
  ]);

  function previewUrlFor(asset) {
    if (!asset) return "";

    if (protectedAsset(asset)) {
      return (
        protectedPreviewUrls[
          clean(asset.id)
        ] || ""
      );
    }

    return safeImageUrl(
      asset.publicUrl
    );
  }

  const unitTypeLabel =
    useCallback(
      (value) =>
        (
          {
            single_page: isFR
              ? "Page"
              : "Page",

            double_page_spread: isFR
              ? "Double page"
              : "Spread",

            front_matter: isFR
              ? "Page liminaire"
              : "Front matter",

            back_matter: isFR
              ? "Page finale"
              : "Back matter",

            activity_page: isFR
              ? "Page d’activité"
              : "Activity page",
          }[value] ||
          (
            isFR
              ? "Unité"
              : "Unit"
          )
        ),
      [isFR]
    );

  const previewItems =
    useMemo(
      () => [
        {
          id: "front-cover",
          kind: "cover",
          asset: activeCover,
          positionLabel: isFR
            ? "Couverture"
            : "Cover",
        },

        ...units.map(
          (unit, index) => ({
            id:
              clean(unit?.id) ||
              `unit-${index + 1}`,

            kind: "unit",
            unit,
            manuscript:
              manuscriptsByUnit[
                unit.id
              ] || null,

            illustration:
              illustrationsByUnit[
                unit.id
              ] || null,

            positionLabel:
              `${unitTypeLabel(
                unit.unitType
              )} ${index + 1} / ${units.length}`,
          })
        ),
      ],
      [
        activeCover,
        illustrationsByUnit,
        isFR,
        manuscriptsByUnit,
        unitTypeLabel,
        units,
      ]
    );

  function renderCover(item) {
    const coverUrl =
      previewUrlFor(item.asset);

    return (
      <div
        style={{
          minHeight: 420,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "100%",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.1em",
              textTransform:
                "uppercase",
              color: "#b45309",
            }}
          >
            {isFR
              ? "Couverture avant"
              : "Front Cover"}
          </div>

          <h3
            className="ns-card-title"
            style={{
              margin: "8px 0 4px",
              fontSize:
                "clamp(24px, 4vw, 38px)",
            }}
          >
            {bookTitle}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: "0 0 18px",
            }}
          >
            {authorName}
          </p>

          {coverUrl ? (
            <img
              src={coverUrl}
              alt={
                isFR
                  ? `Couverture de ${bookTitle}`
                  : `${bookTitle} cover`
              }
              style={{
                display: "block",
                width: "auto",
                maxWidth: "min(420px, 100%)",
                maxHeight: 580,
                margin: "0 auto",
                objectFit: "contain",
                borderRadius: 12,
              }}
            />
          ) : (
            <div
              style={{
                maxWidth: 520,
                margin: "0 auto",
                padding: 28,
                borderRadius: 16,
                background: "#fff7ed",
                border:
                  "1px dashed #fb923c",
              }}
            >
              <strong>
                {isFR
                  ? "Couverture manquante"
                  : "Cover missing"}
              </strong>

              <p
                className="ns-card-meta"
                style={{
                  margin: "8px 0 14px",
                }}
              >
                {protectedPreviewErrors[
                  clean(item.asset?.id)
                ] ||
                  (
                    isFR
                      ? "Ajoutez ou sélectionnez une couverture pour compléter l’ouverture du livre."
                      : "Add or select a cover to complete the opening of the book."
                  )}
              </p>

              {typeof onOpenCoverPlan ===
              "function" ? (
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={onOpenCoverPlan}
                >
                  {isFR
                    ? "Ouvrir la couverture"
                    : "Open Cover"}
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderUnit(item) {
    const unit = item.unit || {};
    const manuscriptText =
      clean(
        item.manuscript
          ?.manuscriptText
      );

    const requiresImage =
      unit.illustrationPlan &&
      unit.illustrationPlan !==
        "none";

    const illustrationUrl =
      previewUrlFor(
        item.illustration
      );

    const workingTitle =
      clean(unit.workingTitle) ||
      item.positionLabel;

    return (
      <article>
        <div
          style={{
            marginBottom: 18,
          }}
        >
          <div
            style={{
              color: "#64748b",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
            }}
          >
            {item.positionLabel}
          </div>

          <h3
            className="ns-card-title"
            style={{
              margin: "6px 0 0",
            }}
          >
            {workingTitle}
          </h3>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
            alignItems: "stretch",
          }}
        >
          <div
            className="ns-nbb-inner-panel"
            style={{
              minHeight: 320,
              padding: 18,
              borderRadius: 16,
              background: "#f8fafc",
              display: "grid",
              placeItems: "center",
              textAlign: "center",
            }}
          >
            {!requiresImage ? (
              <div>
                <strong>
                  {isFR
                    ? "Aucune illustration prévue"
                    : "No illustration planned"}
                </strong>
              </div>
            ) : illustrationUrl ? (
              <img
                src={illustrationUrl}
                alt={
                  isFR
                    ? `Illustration de ${workingTitle}`
                    : `${workingTitle} illustration`
                }
                style={{
                  display: "block",
                  width: "100%",
                  maxHeight: 540,
                  objectFit: "contain",
                  borderRadius: 10,
                }}
              />
            ) : (
              <div>
                <strong
                  style={{
                    color: "#9a3412",
                  }}
                >
                  {isFR
                    ? "Illustration manquante"
                    : "Illustration missing"}
                </strong>

                <p
                  className="ns-card-meta"
                  style={{
                    margin: "8px 0 14px",
                  }}
                >
                  {protectedPreviewErrors[
                    clean(
                      item.illustration?.id
                    )
                  ] ||
                    (
                      isFR
                        ? "Ajoutez l’illustration liée à cette page ou double page."
                        : "Add the illustration linked to this page or spread."
                    )}
                </p>

                {typeof onOpenIllustrationPlan ===
                "function" ? (
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={
                      onOpenIllustrationPlan
                    }
                  >
                    {isFR
                      ? "Ouvrir les illustrations"
                      : "Open Illustrations"}
                  </button>
                ) : null}
              </div>
            )}
          </div>

          <div
            className="ns-nbb-reading-panel"
            style={{
              minHeight: 320,
              padding:
                "clamp(20px, 4vw, 36px)",
              borderRadius: 16,
              background: "#fffdf8",
              border:
                "1px solid rgba(180,83,9,0.14)",
              display: "grid",
              alignContent: "center",
            }}
          >
            {manuscriptText ? (
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.8,
                  fontSize:
                    "clamp(17px, 2vw, 21px)",
                  color: "#1f2937",
                }}
              >
                {manuscriptText}
              </div>
            ) : (
              <div>
                <strong
                  style={{
                    color: "#9a3412",
                  }}
                >
                  {isFR
                    ? "Texte du manuscrit manquant"
                    : "Manuscript text missing"}
                </strong>

                <p
                  className="ns-card-meta"
                  style={{
                    margin: "8px 0 14px",
                  }}
                >
                  {isFR
                    ? "Ajoutez le texte que le jeune lecteur verra ou entendra."
                    : "Add the words the young reader will see or hear."}
                </p>

                {typeof onOpenManuscriptPlan ===
                "function" ? (
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={
                      onOpenManuscriptPlan
                    }
                  >
                    {isFR
                      ? "Ouvrir le manuscrit"
                      : "Open Manuscript"}
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </article>
    );
  }

  return (
    <>
      {childrensBookAssetsError ? (
        <div
          role="alert"
          className="ns-card"
          style={{
            padding: 14,
            marginBottom: 14,
            borderRadius: 14,
            color: "#b91c1c",
          }}
        >
          {childrensBookAssetsError}
        </div>
      ) : null}

      {childrensBookAssetsLoading ||
      protectedPreviewsLoading ? (
        <div
          role="status"
          className="ns-card-meta"
          style={{
            marginBottom: 14,
            fontWeight: 800,
          }}
        >
          {isFR
            ? "Chargement des ressources du livre…"
            : "Loading book assets…"}
        </div>
      ) : null}

      <CreativeProjectPreviewShell
        lang={lang}
        title={bookTitle}
        items={previewItems}
        renderItem={(item) =>
          item.kind === "cover"
            ? renderCover(item)
            : renderUnit(item)
        }
        onReturn={onOpenBookAssembly}
        onExit={onExit}
        returnLabel={
          isFR
            ? "Retour à l’assemblage"
            : "Return to Book Assembly"
        }
      />
    </>
  );
}
